import type {
  ConnectionDisconnectedReason,
  ConnectionState,
  IAgoraRTCClient,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
  NetworkQuality,
  SDK_MODE,
  UID
} from '../agora.types';

import { AGORA, AGORA_EVENTS } from '@/constants';
import store from '@/store';
import { SDRecordingStatus } from '@/store/modules/mediaroom/types';

export enum MediaUserRole {
  audience = 'audience',
  host = 'host'
}

export interface MediaRoom {
  client: IAgoraRTCClient;
  screenShareClient: IAgoraRTCClient;
  mode: SDK_MODE;
  /**
   * channelId|channelSuffix
   */
  channelName: string;
  remoteStreams: {
    [uid: string]: RemoteStream;
  };
  userRole: MediaUserRole;
  isMuted: boolean;
  activeSpeakerId: UID;
  speakerOutDeviceId: string;
  connectionStrength: number;
  status?: ConnectionState;
  connectionDisconnectedReason?: ConnectionDisconnectedReason;
  screenStatus?: ConnectionState;
  screenConnectionDisconnectedReason?: ConnectionDisconnectedReason;
  desiredStatus?: ConnectionState;
  id: Number;
  label?: any;
}

export interface RemoteMediaStream extends RemoteStream {
  loading?: Boolean;
  showControls?: Boolean;
  signalStrength?: Number;
  id: String;
}

export type AgoraToken = {
  token: string;
  suffix: string;
  room: string;
  channel_suffix: string;
};

type ActiveSpeakerVolume = {
  uid: UID;
  level: number;
};

type ActiveSpeakerTrack = {
  trackStatus?: string;
};

export interface ActiveSpeakerUpdate {
  detectedSpeaker?: string;
  activeSpeakerId?: string;
  maxRemoteAudioLevel: number;
  lastSpokenAt: number;
}

export interface RtcDevices {
  microphones: MediaDeviceInfo[];
  cameras: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

type RemoteStream = {
  /**
   * Missing audioTrack means that the stream audio is muted
   */
  audioTrack?: IRemoteAudioTrack;
  /**
   * Missing videoTrack means that the stream video is muted
   */
  videoTrack?: IRemoteVideoTrack;
  user?: any;
  networkQuality?: NetworkQuality;
  audioLevel?: number;
};

export function getActiveSpeakerUpdate({
  volumes,
  localTracks,
  localAudioLevel,
  currentUserId,
  lastActiveSpeakerId,
  lastSpokenAt,
  now = Date.now()
}: {
  volumes: ActiveSpeakerVolume[];
  localTracks: ActiveSpeakerTrack[];
  localAudioLevel?: number;
  currentUserId?: string;
  lastActiveSpeakerId: string | null;
  lastSpokenAt: number;
  now?: number;
}): ActiveSpeakerUpdate {
  const volumeSensitivity = 3;
  const silenceHoldDuration = 1500;
  let detectedSpeaker: string | undefined;
  let maxRemoteAudioLevel = 0;

  volumes.forEach(volume => {
    if (volume.level > maxRemoteAudioLevel && volume.level > volumeSensitivity) {
      detectedSpeaker = volume.uid.toString();
      maxRemoteAudioLevel = volume.level;
    }
  });

  const hasPublishedLocalAudio = localTracks.some(track => track.trackStatus === 'PUBLISHED');
  if (hasPublishedLocalAudio && localAudioLevel && localAudioLevel > maxRemoteAudioLevel) {
    detectedSpeaker = currentUserId;
  }

  if (detectedSpeaker) {
    return {
      detectedSpeaker,
      activeSpeakerId: detectedSpeaker,
      maxRemoteAudioLevel,
      lastSpokenAt: now
    };
  }

  return {
    activeSpeakerId: lastActiveSpeakerId && now - lastSpokenAt < silenceHoldDuration ? lastActiveSpeakerId : undefined,
    maxRemoteAudioLevel,
    lastSpokenAt
  };
}

export function mapConnectionStatesToSDStatus(
  peerConnectionState: RTCPeerConnectionState,
  connectionState: ConnectionState,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
): SDRecordingStatus {
  if (peerConnectionState === 'connected' && connectionState === 'CONNECTED') {
    return SDRecordingStatus.CONNECTED;
  }

  if (connectionState === 'DISCONNECTED' && connectionDisconnectedReason === 'LEAVE') {
    return SDRecordingStatus.IDLE;
  }

  if (
    peerConnectionState === 'disconnected' ||
    peerConnectionState === 'failed' ||
    peerConnectionState === 'closed' ||
    connectionState === 'DISCONNECTED' ||
    connectionState === 'DISCONNECTING'
  ) {
    return SDRecordingStatus.FAILED;
  }

  if (peerConnectionState === 'connecting' || connectionState === 'CONNECTING' || connectionState === 'RECONNECTING') {
    return SDRecordingStatus.CONNECTING;
  }

  return SDRecordingStatus.IDLE;
}

export function updateSDRecordingStatus(
  feed: 'user' | 'screenshare',
  channelName: string,
  isScreenShare: boolean,
  connectionDisconnectedReason?: ConnectionDisconnectedReason
): void {
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return;
  }

  const channelState = store.state.mediaroom.channelPeerConnectionStates[channelName];
  const mediaRoom = store.state.mediaroom.mediaRooms[channelName];

  if (!channelState || !mediaRoom) {
    return;
  }

  const peerConnectionState = channelState.peerConnectionState || 'new';
  const connectionState = isScreenShare ? mediaRoom.screenStatus : mediaRoom.status;
  const disconnectReason =
    connectionDisconnectedReason ||
    (isScreenShare ? mediaRoom.screenConnectionDisconnectedReason : mediaRoom.connectionDisconnectedReason);

  if (!connectionState) {
    return;
  }

  store.commit('mediaroom/setSDRecordingStatus', {
    feed,
    status: mapConnectionStatesToSDStatus(peerConnectionState, connectionState, disconnectReason)
  });
}

export function getUserSuffixForAgoraToken(isScreen: Boolean = false): string {
  let userSuffix = '';
  if (store.state.event.stage?.isTVMode) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.TV_SCREEN;
  } else if (store.state.event.stage?.isLiveStreamMode) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.LIVE_STREAMER;
  }
  if (isScreen) {
    userSuffix = userSuffix + AGORA.UID_SUFFIX.SCREEN_STREAM;
    if (store.state.route.query.record_mode === 'true') {
      userSuffix = userSuffix + AGORA.UID_SUFFIX.RECORDER;
    }
  }
  // 4 digit random suffix to avoid clashes from 2 tabs
  userSuffix = '|' + Math.random().toString(36).slice(2, 6) + userSuffix;
  return userSuffix;
}

export function wrapClientForMock(client: IAgoraRTCClient): IAgoraRTCClient {
  const listeners: Record<string, Function[]> = {};
  let currentConnectionState: ConnectionState = 'DISCONNECTED';

  return new Proxy(client, {
    get(target, prop, receiver) {
      if (prop === 'connectionState') {
        return currentConnectionState;
      }
      if (prop === 'on') {
        return (event: string, handler: Function) => {
          if (!listeners[event]) listeners[event] = [];
          listeners[event].push(handler);
          return target.on ? target.on(event as any, handler as any) : undefined;
        };
      }
      if (prop === 'removeAllListeners') {
        return (event?: string) => {
          if (event) {
            delete listeners[event];
          } else {
            Object.keys(listeners).forEach(k => delete listeners[k]);
          }
          return target.removeAllListeners ? target.removeAllListeners(event as any) : undefined;
        };
      }
      if (prop === 'join') {
        return async (_appId: string, _channel: string, _token: string, _uid: string) => {
          currentConnectionState = 'CONNECTED';
          setTimeout(() => {
            listeners['connection-state-change']?.forEach(fn => fn('CONNECTED', 'DISCONNECTED'));
            listeners[AGORA_EVENTS.PEER_CONNECTION_STATE_CHANGE]?.forEach(fn => fn('connected', 'new'));
          }, 50);
          return 12345;
        };
      }
      if (prop === 'leave') {
        return async () => {
          currentConnectionState = 'DISCONNECTED';
          listeners['connection-state-change']?.forEach(fn => fn('DISCONNECTED', 'CONNECTED', 'LEAVE'));
          listeners[AGORA_EVENTS.PEER_CONNECTION_STATE_CHANGE]?.forEach(fn => fn('disconnected', 'connected'));
        };
      }
      if (prop === 'publish' || prop === 'unpublish' || prop === 'subscribe') {
        return async () => undefined;
      }
      if (prop === 'setClientRole') {
        return async () => undefined;
      }
      if (
        prop === 'setStreamFallbackOption' ||
        prop === 'enableAudioVolumeIndicator' ||
        prop === 'startProxyServer' ||
        prop === 'setTurnServer'
      ) {
        return () => undefined;
      }
      if (prop === 'getRemoteNetworkQuality') {
        return () => ({});
      }
      const val = Reflect.get(target, prop, receiver);
      if (typeof val === 'function') {
        return val.bind(target);
      }
      return val;
    }
  });
}

export function isAnyMediaRoomReconnecting(): boolean {
  const mediaRooms = store.state.mediaroom?.mediaRooms;
  let isReconnecting = false;
  Object.keys(mediaRooms).forEach(roomId => {
    const room: MediaRoom = mediaRooms[roomId];
    if (room.desiredStatus !== 'CONNECTED') {
      return;
    }
    if (['RECONNECTING', 'DISCONNECTED'].includes(room.status || '')) {
      isReconnecting = true;
    }
  });

  return isReconnecting;
}
